import { Router } from '@angular/router';
import { AppStateService, HierarchyMenuItem } from '@siza-staff-care/shared/app-state';
import { menuStructure } from './menu-structure';

type FlattenedItem = {
	route: string;
	routeKey: string;
	securityMapping: string;
};

let initialised = false;

let keysByRoute: Record<string, string>;
let routeKeysByRoute: Record<string, string>;

/**
 * Uses the current root Router URL to get the security mapping - being the old Jade Web Forms form class name.
 * planned-rota -> Schedules
 * The URL is modified using a regex to strip off any leading slash and any query params or secondary routes.
 * @param router
 * @param appState
 * @returns
 */
export const getCurrentSecurityMapping = (router: Router, appState: AppStateService) => {
	const fixedUrl = getNormalisedRoute(router, appState);
	return getKeyForRootRoute(fixedUrl, appState);
};

/**
 * Gets the normalised route from the current root Router URL.
 * The URL is modified using a regex to strip off any leading slash and any query params or secondary routes.
 *
 * @param router
 * @param appState
 * @returns
 */
export const getNormalisedRoute = (router: Router, appState: AppStateService) => {
	checkInitialised(appState);

	// Get the root route - e.g. staff-records
	const url = appState.securityRouteOverride || router.routerState.snapshot.url;
	return normaliseRoute(url);
};

/**
 * Normalises a route URL by stripping off any leading slash and any query params or secondary routes.
 * @param route
 * @returns
 */
export const normaliseRoute = (route: string) => {
	// Modify the URL using a regex to strip off any leading slash and any query params or secondary routes.
	return route.replace(/^\/?([^?(/]*)([?(/].*)?$/, '$1');
};

/**
 * Converts a route URL into a security mapping - being the old Jade Web Forms form class name.
 */
export const getKeyForRootRoute = (route: string, appState: AppStateService, opts?: { useRouteKey: boolean }) => {
	checkInitialised(appState);
	return opts?.useRouteKey ? routeKeysByRoute[route] : keysByRoute[route];
};

const checkInitialised = (appState: AppStateService) => {
	if (initialised) {
		return;
	}

	const settings = appState.settings;

	const flattenedItems = buildFlatItems(
		menuStructure(
			settings.allocationManagementScreenName ?? '',
			settings.allocationManagementName ?? '',
			settings.sexAsGender,
		),
	);
	keysByRoute = flattenedItems.reduce((dict, item) => {
		dict[item.route] = item.securityMapping;
		return dict;
	}, {});
	routeKeysByRoute = flattenedItems.reduce((dict, item) => {
		dict[item.route] = item.routeKey;
		return dict;
	}, {});

	initialised = true;
};

const buildFlatItems = (items: HierarchyMenuItem[]): FlattenedItem[] => {
	if (!items) {
		return [];
	}

	const flatten = (item: HierarchyMenuItem, result: FlattenedItem[]) => {
		if (item.route) {
			const key = item.controlsKey ?? item.key;

			// Construct the flattened item.
			const newItem: FlattenedItem = {
				route: item.route,
				routeKey: item.key,
				securityMapping: key,
			};
			result.push(newItem);
		}

		if (!item.children) {
			return;
		}

		// Recurse into the children.
		item.children.forEach((child) => flatten(child, result));
	};

	const result = [];
	items.forEach((i) => {
		flatten(i, result);
	});

	return result;
};
